import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { useContext, useEffect, useRef, useState } from "react";
import CustomButton from "../../components/CustomButton";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import SectionDivider from "../../components/SectionDivider";
import PDFUploader from "../../components/PDFUploader";
import InformationBox from "../../components/InformationBox";
import Modal from "../../components/Modal";
import classes3 from "../../components/PDFUploader.module.css";
import deleteIcon from "../../assets/circled_cancel.svg";
import uploadIcon from "../../assets/upload_icon.png";
import closeIcon from "../../assets/close_icon.png";
import { DoneIcon, WarningIcon } from "../../assets/registtyIcons/icons";
import { useNavigate, useParams } from "react-router-dom";
import CustomInput from "../../components/CustomInput";
import saveIcon from "../../assets/save_icon.png";
import editIcon from "../../assets/edit_icon.png";
import trashIcon from "../../assets/delete_Icon.png";
import { useInvoices } from "./Invoices";
import invoicePdf from "../ai-organization/files/inovice.pdf";
import bankApplicationPDF from "../ai-organization/files/bank-application.pdf";
import cirtificate from "../ai-organization/files/supplierCertificate.pdf";
import associationPdf from "../ai-organization/files/articleOfAsscosiation.pdf";
import confirm from "./files/confirm.pdf";
import moneyTransactionRequest from "./files/moneyTransactionRequest.pdf";
import declarationAndUndertaking from "./files/declarationAndUndertaking.pdf";
import PDFViewer from "../../components/PDF";
import sendIcon from "../../assets/send_icon.png";

const urlToFile = async (url, filename, type = "application/pdf") => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], filename, { type });
};

const AIOrganizationInvoicePage = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { invoices, setInvoices } = useInvoices();
  const { id } = useParams();

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Fetch files and create object URLs
      const [
        invoiceBlob,
        bankAppPdfBlob,
        certBlob,
        assocBlob,
        file1Blob,
        file2Blob,
        declarationAndUndertakingBlob,
      ] = await Promise.all([
        urlToFile(invoicePdf, "invoice.pdf", "application/pdf"),
        urlToFile(
          bankApplicationPDF,
          "bank-application.pdf",
          "application/pdf"
        ),
        urlToFile(cirtificate, "supplierCertificate.pdf", "application/pdf"),
        urlToFile(
          associationPdf,
          "articlesOfAssociation.pdf",
          "application/pdf"
        ),
        urlToFile(confirm, "file-1.pdf", "application/pdf"),
        urlToFile(moneyTransactionRequest, "file-2.pdf", "application/pdf"),
        urlToFile(
          declarationAndUndertaking,
          "declarationAndUndertaking.pdf",
          "application/pdf"
        ),
      ]);

      // Create object URLs
      const invoiceObjectUrl = URL.createObjectURL(invoiceBlob);
      const certObjectUrl = URL.createObjectURL(certBlob);
      const assocObjectUrl = URL.createObjectURL(assocBlob);
      const bankAppPdfObjectUrl = URL.createObjectURL(bankAppPdfBlob);
      const file1ObjectUrl = URL.createObjectURL(file1Blob);
      const file2ObjectUrl = URL.createObjectURL(file2Blob);
      const declarationAndUndertakingObjectUrl = URL.createObjectURL(
        declarationAndUndertakingBlob
      );

      const data = {
        id,
        invoiceNumber: invoices.find((invoice) => invoice.id === id)
          ?.invoiceNumber,
        docs: [
          {
            id: 1,
            name: "invoice.pdf",
            filename: "invoice",
            path: invoiceObjectUrl,
            objectUrl: invoiceObjectUrl,
            size: invoiceBlob.size,
            mime_type: "application/pdf",
            image_type: "invoice",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "b925f0fc-1a95-490d-9624-fc148571f234",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 2,
            name: "bank-application.pdf",
            filename: "financialTransferRequestForm",
            path: bankAppPdfObjectUrl,
            objectUrl: bankAppPdfObjectUrl,
            size: bankAppPdfBlob.size,
            mime_type:
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            image_type: "bank application",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 3,
            name: "supplierCertificate.pdf",
            filename: "supplierCertificate",
            path: certObjectUrl,
            objectUrl: certObjectUrl,
            size: certBlob.size,
            mime_type: "application/pdf",
            image_type: "certificate",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 4,
            name: "articlesOfAssociation.pdf",
            filename: "articlesOfAssociation",
            path: assocObjectUrl,
            objectUrl: assocObjectUrl,
            size: assocBlob.size,
            mime_type: "application/pdf",
            image_type: "association",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 5,
            name: "confirm.pdf",
            filename: "confirmDocument",
            path: file1ObjectUrl,
            objectUrl: file1ObjectUrl,
            size: file1Blob.size,
            mime_type: "application/pdf",
            image_type: "association",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 6,
            name: "moneyTransactionRequest.pdf",
            filename: "moneyTransactionRequest",
            path: file2ObjectUrl,
            objectUrl: file2ObjectUrl,
            size: file2Blob.size,
            mime_type: "application/pdf",
            image_type: "association",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 7,
            name: "declarationAndUndertaking.pdf",
            filename: "declarationAndUndertaking",
            path: declarationAndUndertakingObjectUrl,
            objectUrl: declarationAndUndertakingObjectUrl,
            size: declarationAndUndertakingBlob.size,
            mime_type: "application/pdf",
            image_type: "association",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          }
        ],
      };

      setInvoiceDocs(data?.docs || []);
      setLoading(false);
    } catch (error) {
      console.log("error fetching invoices", error);
      setError("errorFetchingInvoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [pagination.currentPage]);

  const handleSave = (data) => {
    console.log(data);
    setInvoices((prev) =>
      prev.map((inv) => (inv.id === invoice.id ? { ...inv, ...data } : inv))
    );
    setShowUploadModal(false);
  };

  const handleSend = () => {
    setInvoices((prev) =>
      prev.map((invoice) =>
        invoice.id === id ? { ...invoice, status: "sent" } : invoice
      )
    );
  };

  const invoice = invoices.find((invoice) => invoice.id === id) || {};

  return (
    <PageWrapper
      style={{ direction: currentDirection }}
      title={t("transactionsPage")}
      withBackButton
    >
      <div className="u-margin--top_500 o-row o-row--gap-size_200">
        <span>{t("transactionNumber")}:</span>
        <span className={`t-bold_semi-bold`}>{invoice?.invoiceNumber}</span>
      </div>
      <section className="u-margin--top_700">
        {loading && <Loader size={LOADER_SIZES.MEDIUM} />}
        {error && <ErrorBox>{t(error)}</ErrorBox>}

        {!loading && !error && invoiceDocs.length === 0 && (
          <InformationBox>{t("invoiceHasNoDocs")}</InformationBox>
        )}

        {!loading && !error && invoiceDocs.length > 0 && (
          <>
            <div className="o-grid o-grid--auto-columns_xsmall o-grid--gap-size_700">
              <PDFViewer
                path={invoiceDocs[0].path}
                filename={invoiceDocs[0].filename}
              />

              <PDFViewer
                path={invoiceDocs[1].path}
                filename={invoiceDocs[1].filename}
              />

              <PDFViewer
                path={invoiceDocs[2].path}
                filename={invoiceDocs[2].filename}
              />

              <PDFViewer
                path={invoiceDocs[3].path}
                filename={invoiceDocs[3].filename}
              />

              <PDFViewer
                path={invoiceDocs[4].path}
                filename={invoiceDocs[4].filename}
              />

              <PDFViewer
                path={invoiceDocs[5].path}
                filename={invoiceDocs[5].filename}
              />

              <PDFViewer
                path={invoiceDocs[6].path}
                filename={invoiceDocs[6].filename}
              />
            </div>
            {invoice.status !== "sent" && (
              <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end u-padding--bottom_500">
                <CustomButton
                  title={t("edit")}
                  variant="blank"
                  icon={editIcon}
                  onClick={() => setShowUploadModal(true)}
                  iconSize={18}
                />

                <CustomButton
                  title={t("send")}
                  onClick={handleSend}
                  icon={sendIcon}
                  iconSize={15}
                />
              </div>
            )}

            {invoice.status === "sent" && (
              <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end u-padding--bottom_500 o-row--vertical-align_center">
                <DoneIcon width={30} height={30} />
                <span>{t("invoiceSent")}</span>
              </div>
            )}
          </>
        )}
        {showUploadModal && (
          <UploadApplicationsDocumentsModal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            onSave={handleSave}
            title="uploadApplicationsDocuments"
            modalSize="medium"
            invoice={{
              invoiceNumber: invoice.invoiceNumber,
              files: {
                invoice: invoiceDocs[0],
                bankForm: invoiceDocs[1],
                articlesOfAssociation: invoiceDocs[2],
                supplierCertificate: invoiceDocs[3],
                confirm: invoiceDocs[4],
                moneyTransactionRequest: invoiceDocs[5],
                declarationAndUndertaking: invoiceDocs[6],
              },
            }}
            invoiceId={invoice.invoiceNumber}
          />
        )}
      </section>
    </PageWrapper>
  );
};

function UploadApplicationsDocumentsModal({
  onClose,
  modalSize = "medium",
  invoice = {},
  invoiceId,
  onSave,
}) {
  const { t } = useTranslation("general");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatingError, setUpdatingError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingError, setDeletingError] = useState("");
  const [files, setFiles] = useState(invoice.files);
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoiceNumber);
  const { invoices, setInvoices } = useInvoices();
  const navigate = useNavigate();

  const handleClose = () => {
    setFiles({
      invoice: null,
      bankForm: null,
      articlesOfAssociation: null,
      supplierCertificate: null,
      confirm: null,
      moneyTransactionRequest: null,
      declarationAndUndertaking: null,
    });
    setUpdatingError("");
    onClose();
  };

  const handleFileChange = (fieldName) => (event) => {
    const file = event.target.files[0];
    setFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    // Clear error when user selects a file
    if (updatingError) setUpdatingError("");
  };

  const handleDeleteFile = (fieldName) => {
    setFiles((prev) => ({
      ...prev,
      [fieldName]: null,
    }));

    // Reset the file input value using direct DOM access
    const fileInput = document.getElementById(fieldName);
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setUpdatingError("");

      if (!invoiceNumber) {
        setUpdatingError("invoiceNumberRequired");
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();

      Object.keys(files).forEach((fieldName) => {
        if (files[fieldName]) {
          formData.append(fieldName, files[fieldName]);
        }
      });

      const response = await new Promise((r) => {
        setTimeout(() => {
          r({
            status: 200,
            data: {
              invoiceNumber,
              files,
            },
          });
        }, 2000);
      });

      onSave?.({
        invoiceNumber,
      });

      handleClose();
    } catch (err) {
      console.log(err);
      setUpdatingError("errorSavingInvoice");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setDeletingError("");
      setUpdatingError("");

      const response = await new Promise((r) => {
        setTimeout(() => {
          r({
            status: 200,
            data: {
              invoiceNumber,
            },
          });
        }, 2000);
      });

      setInvoices((prev) =>
        prev.filter((invoice) => invoice.invoiceNumber !== invoiceId)
      );

      navigate("/ai-organization-dashboard/invoices", {
        replace: true,
      });

      handleClose();
    } catch (err) {
      console.log(err);
      setDeletingError("errorDeletingInvoice");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderFileUpload = (fieldName, labelKey) => (
    <div className="o-column u-margin--top_200">
      <SectionDivider title={t(labelKey)} noMargin={true} />

      <div className="o-column o-column--gap-size_100 u-padding_0">
        <div className="u-width--max-width_300">
          <input
            type="file"
            id={fieldName}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={handleFileChange(fieldName)}
            className={`${classes3.fileInput} u-padding_0`}
          />
          <div>
            <label htmlFor={fieldName}>
              <p className={` ${classes3.customFileInput}`}>
                <img src={uploadIcon} alt={`Upload image`} />
                <span>{t("uploadDocument")}</span>
              </p>
            </label>
          </div>

          <div className="u-width--max-width_300 o-row o-row--vertical-align_center">
            <div className="o-row o-row--vertical-align_center">
              {files[fieldName] ? (
                <span className="">
                  <DoneIcon width={30} height={30} />
                  {/* {t("fileSelected")} */}
                </span>
              ) : (
                <span className="u-color_success u-font-size_small o-row o-row--vertical-align_center o-row--gap-size_200">
                  <WarningIcon width={20} height={20} />
                  {t("noFileSelected")}
                </span>
              )}
            </div>
            {files[fieldName] && (
              <div className={classes3.fileName}>
                {t("fileName")} : {files[fieldName]?.name?.slice(0, 30)}
                <button
                  className={classes3.deleteFileButton}
                  onClick={() => handleDeleteFile(fieldName)}
                >
                  <img src={deleteIcon} className="u-height-specific_20" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const isLoading = isSubmitting || isDeleting;
  const error = updatingError || deletingError;

  return (
    <Modal
      title={t("editTransactionDocuments")}
      onClose={handleClose}
      modalSize={modalSize}
      error={t(error)}
      footer={
        <>
          {isLoading && <Loader size={LOADER_SIZES.MEDIUM} />}
          <section className="o-row o-row--gap-size_300 o-row--horizontal-align_end">
            <CustomButton
              title={t("delete")}
              onClick={handleDelete}
              icon={trashIcon}
              iconSize={20}
              disabled={isSubmitting}
              variant={"red"}
            />
            <CustomButton
              title={t("cancel")}
              onClick={handleClose}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
            <CustomButton
              title={t("save")}
              onClick={handleSave}
              icon={saveIcon}
              iconSize={20}
              disabled={isSubmitting}
            />
          </section>
        </>
      }
    >
      {error && <ErrorBox>{t(error)}</ErrorBox>}
      <div className="o-column o-column--gap-size_300">
        <CustomInput
          label={t("invoiceNumber")}
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          title={t("invoiceNumber")}
          errorText={
            updatingError === "invoiceNumberRequired"
              ? t("invoiceNumberRequired")
              : null
          }
        />

        <div className=" u-margin--top_500" />

        <SectionDivider title="sectionOne" noMargin={true} />
        <div className="o-column o-column--gap-size_200 u-margin--bottom_500">
          {renderFileUpload("invoice", "invoice")}
          {renderFileUpload("bankForm", "financialTransferRequestForm")}
          {renderFileUpload("supplierCertificate", "supplierCertificate")}
          {renderFileUpload("articlesOfAssociation", "articlesOfAssociation")}
        </div>

        <SectionDivider title="sectionTwo" noMargin={true} />
        <div className="o-column o-column--gap-size_200">
          {renderFileUpload("confirm", "confirmDocument")}
          {renderFileUpload(
            "moneyTransactionRequest",
            "moneyTransactionRequest"
          )}
          {renderFileUpload(
            "declarationAndUndertaking",
            "declarationAndUndertaking"
          )}
        </div>
      </div>
    </Modal>
  );
}

export default AIOrganizationInvoicePage;
